import { countTransform } from '@plugins/client/pipes/math/abbreviate-number.pipe.utils';
import {
  defaultUserDetailsTranslations,
  type UserDetailsTranslations,
  type UserDetailsTranslationsShape,
} from '@plugins/userdetails-lib/translations';

import { AllEntityTypesWithFails } from './types.private';

// Helper defined in separate file, because it is used in Jest and Playwright
// and Playwright will refuse to compile files that have Angular imports or imports
// in which decorators are used at any point. See https://github.com/microsoft/playwright/issues/29646

type ComposeAtomicArgs = {
  entity: AllEntityTypesWithFails | undefined;
  detailed: boolean;
  translations: UserDetailsTranslationsShape;
  locale?: string;
};

export const atomicTranslation = ({
  entity,
  detailed,
  translations,
  locale,
}: ComposeAtomicArgs) => {
  let composed = '',
    count: string | undefined;
  if (!entity) {
    console.warn("No entity provided, 'INVALID' string will be returned");
    return 'INVALID';
  }
  if (entity.type === 'error') {
    return translations.error.requestError;
  }
  if (entity.type === 'missing') {
    return translations.missing;
  }
  if (entity.type === 'group') {
    composed =
      translations[
        detailed
          ? entity.userCount === 1
            ? 'groupDetailSingle'
            : 'groupDetailNonSingle'
          : 'group'
      ];

    count = entity.userCount
      ? countTransform(entity.userCount, locale ?? 'en-US')
      : undefined;
  }
  if (entity.type === 'user') {
    composed = translations[detailed ? 'userDetail' : 'user'];
  }

  const vars = { name: entity.name, count };

  Object.entries(vars).forEach(([key, val]) => {
    return (composed = composed.replaceAll(`{{${key}}}`, String(val)));
  });
  return composed;
};

export const composeAtomicTranslation = (
  entity: AllEntityTypesWithFails,
  detailed: boolean,
  language: {
    translations: UserDetailsTranslations;
    lang: keyof UserDetailsTranslations;
    locale?: string;
  } = {
    translations: defaultUserDetailsTranslations,
    lang: 'en',
  },
): string => {
  return atomicTranslation({
    entity,
    detailed,
    translations: language.translations[language.lang],
    locale: language.locale,
  });
};

export const getNameInitials = (entity?: AllEntityTypesWithFails): string => {
  if (!entity || entity.type === 'error') return '';
  const { name } = entity;
  if (!name) return '';
  const nameSplit = name.trim().split(' ');
  const length = nameSplit.length;
  if (length === 1) {
    return nameSplit[0].slice(0, 2);
  }
  if (length >= 2) {
    const [first, last] = [nameSplit[0][0], nameSplit[length - 1][0]];
    return `${first}${last}`;
  }
  return '';
};
